import { isDefinedString } from "et3-type-guards";
import queryString from "query-string";
import { isNumber, memoize } from "lodash";
import { observable, runInAction } from "mobx";
import {
  apiCallGetUserPaymentsByInvoiceNumber,
  apiCallGetUserPaymentsByTransactionId,
  apiCallGetUserPaymentsByUserID
} from "../../../api/user-api/api-call-get-user-payments";

import { TUserSubscriptionPaymentHistory } from "../../../Types";

import getCoreNavigationStore from "../../core/core-navigation-store";
import getCorePopUpStore from "../../core/core-popup-store";
import getCoreUIStore from "../../core/core-ui-store";
import { TEt3PickerSingleValue } from "../../../TypesNEW";
import { reducerToZeroIfUndefined } from "../../../helpers/et3-picker-data-helper";

class UserPaymentsStore {
  userIdInput = observable.box<number>(-1);
  transactionIdInput = observable.box<string>("");
  invoiceNumberInput = observable.box<string>("");
  paymentLimit = observable.box<TEt3PickerSingleValue>({
    label: "5",
    value: "5"
  });
  userPaymentData = observable.box<{
    silverSubscriptionPaymentHistory: TUserSubscriptionPaymentHistory;
    goldSubscriptionPaymentHistory: TUserSubscriptionPaymentHistory;
    platinumSubscriptionPaymentHistory: TUserSubscriptionPaymentHistory;
    powerBundleSubscriptionPaymentHistory: TUserSubscriptionPaymentHistory;
    readSubscriptionPaymentHistory: TUserSubscriptionPaymentHistory;
    vipSubscriptionPaymentHistory: TUserSubscriptionPaymentHistory;
    plusSubscriptionPaymentHistory: TUserSubscriptionPaymentHistory;
    khattabatSubscriptionPaymentHistory: TUserSubscriptionPaymentHistory;
    boostSubscriptionPaymentHistory: TUserSubscriptionPaymentHistory;
    rewindSubscriptionPaymentHistory: TUserSubscriptionPaymentHistory;
    otherSubscriptionPaymentHistory: TUserSubscriptionPaymentHistory;
    userID: number;
  }>({
    silverSubscriptionPaymentHistory: [],
    goldSubscriptionPaymentHistory: [],
    platinumSubscriptionPaymentHistory: [],
    powerBundleSubscriptionPaymentHistory: [],
    readSubscriptionPaymentHistory: [],
    vipSubscriptionPaymentHistory: [],
    plusSubscriptionPaymentHistory: [],
    khattabatSubscriptionPaymentHistory: [],
    boostSubscriptionPaymentHistory: [],
    rewindSubscriptionPaymentHistory: [],
    otherSubscriptionPaymentHistory: [],
    userID: -1
  });

  setInvoiceNumber = (invoiceNumber: string) => {
    runInAction(() => {
      this.invoiceNumberInput.set(invoiceNumber);
    });
  };
  setPaymentLimit = (limit: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.paymentLimit.set(limit);
    });
  };
  setPaymentData = (data: {
    silverSubscriptionPaymentHistory: TUserSubscriptionPaymentHistory;
    goldSubscriptionPaymentHistory: TUserSubscriptionPaymentHistory;
    platinumSubscriptionPaymentHistory: TUserSubscriptionPaymentHistory;
    powerBundleSubscriptionPaymentHistory: TUserSubscriptionPaymentHistory;
    readSubscriptionPaymentHistory: TUserSubscriptionPaymentHistory;
    vipSubscriptionPaymentHistory: TUserSubscriptionPaymentHistory;
    plusSubscriptionPaymentHistory: TUserSubscriptionPaymentHistory;
    khattabatSubscriptionPaymentHistory: TUserSubscriptionPaymentHistory;
    boostSubscriptionPaymentHistory: TUserSubscriptionPaymentHistory;
    rewindSubscriptionPaymentHistory: TUserSubscriptionPaymentHistory;
    otherSubscriptionPaymentHistory: TUserSubscriptionPaymentHistory;
    userID: number;
  }) => {
    runInAction(() => {
      this.userPaymentData.set({
        silverSubscriptionPaymentHistory: data.silverSubscriptionPaymentHistory,
        goldSubscriptionPaymentHistory: data.goldSubscriptionPaymentHistory,
        platinumSubscriptionPaymentHistory:
          data.platinumSubscriptionPaymentHistory,
        powerBundleSubscriptionPaymentHistory:
          data.powerBundleSubscriptionPaymentHistory,
        readSubscriptionPaymentHistory: data.readSubscriptionPaymentHistory,
        vipSubscriptionPaymentHistory: data.vipSubscriptionPaymentHistory,
        plusSubscriptionPaymentHistory: data.plusSubscriptionPaymentHistory,
        khattabatSubscriptionPaymentHistory:
          data.khattabatSubscriptionPaymentHistory,
        boostSubscriptionPaymentHistory: data.boostSubscriptionPaymentHistory,
        rewindSubscriptionPaymentHistory: data.rewindSubscriptionPaymentHistory,
        otherSubscriptionPaymentHistory: data.otherSubscriptionPaymentHistory,
        userID: data.userID
      });
    });
  };
  checkURLSearch = async (search: string) => {
    if (isDefinedString(search)) {
      const searchID = queryString.parse(search).id;
      if (isDefinedString(searchID) && searchID !== "0") {
        const searchIDNumber = parseInt(searchID, 10);
        getUserPaymentsStore().setUserIDInput(searchIDNumber);
        await getUserPaymentsStore().getUserPaymentByUserId();
      }
    }
  };

  searchUserPaymentsByUserId = async () => {
    await this.getUserPaymentByUserId();
    getCoreNavigationStore().setURLSearchID(this.userIdInput.get());
  };

  getUserPaymentByUserId = async () => {
    const userId = this.userIdInput.get();
    const limit = reducerToZeroIfUndefined(
      parseInt(this.paymentLimit.get().value, 10)
    );
    if (!isNumber(userId) || userId === -1) {
      getCorePopUpStore().showAlertMessage("WRONG_USER_ID");
      return;
    }
    getCoreUIStore().setIsLoading("USER_PAYMENTS", true);

    const { data, code, status } = await apiCallGetUserPaymentsByUserID({
      userId,
      limit
    });

    this.setPaymentData(data);
    getCoreUIStore().setIsLoading("USER_PAYMENTS", false);
    if (status === "ERROR") {
      getCorePopUpStore().showAlertMessage(code);
    }
  };
  getUserPaymentByTransactionId = async () => {
    const transactionId = this.transactionIdInput.get();
    const limit = reducerToZeroIfUndefined(
      parseInt(this.paymentLimit.get().value, 10)
    );
    if (!isDefinedString(transactionId) || transactionId === "") {
      return;
    }
    getCoreUIStore().setIsLoading("USER_PAYMENTS", true);
    const { data, code, status } = await apiCallGetUserPaymentsByTransactionId({
      transactionId,
      limit
    });
    this.setPaymentData(data);
    getCoreUIStore().setIsLoading("USER_PAYMENTS", false);
    if (status === "ERROR") {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  getUserPaymentByInvoiceNumber = async () => {
    const invoiceID = this.invoiceNumberInput.get();
    const limit = reducerToZeroIfUndefined(
      parseInt(this.paymentLimit.get().value, 10)
    );
    if (!isDefinedString(invoiceID) || invoiceID === "") {
      return;
    }
    getCoreUIStore().setIsLoading("USER_PAYMENTS", true);
    const { data, code, status } = await apiCallGetUserPaymentsByInvoiceNumber({
      invoiceID,
      limit
    });
    this.setPaymentData(data);
    getCoreUIStore().setIsLoading("USER_PAYMENTS", false);
    if (status === "ERROR") {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  setUserIDInput = (id: number) => {
    this.userIdInput.set(id);
  };
}

const getUserPaymentsStore = memoize(
  () => new UserPaymentsStore(),
  () => 1
);
export default getUserPaymentsStore;
